import { Suspense } from 'react';

import { Center, Loader } from '@mantine/core';
import { Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useTimeZoneVersion } from '@/shared/hooks/useSessionTimeZone';

import { UnknownTimeZoneNotice } from './UnknownTimeZoneNotice';

/**
 * The content area of a layout (console, portal, platform): the page of the route, inside a
 * suspense boundary of its own. A page that is still loading its chunk (`lazyPage`) suspends here
 * and only the content shows the loader. Without this boundary the nearest one is the router's,
 * above the layout, and the whole screen, menu included, went blank and came back on the first
 * visit to each page (usability review, pending 3).
 *
 * The page is keyed by the zone version of the session (`AM-282`): the dates it shows were
 * formatted with the zone it was rendered under, so when the session is refreshed with another one
 * (the platform moved the tenant's while the session was open) the page is drawn again instead of
 * keeping the old hours until something else repaints it. Only a refresh that brings another zone
 * does it, which is rare: what was typed and not saved in the open page does not survive it. A
 * session that ends does not redraw anything.
 */
export function PageOutlet() {
  const { t } = useTranslation();
  const timeZoneVersion = useTimeZoneVersion();

  return (
    <>
      <UnknownTimeZoneNotice />
      <Suspense
        fallback={
          <Center py="xl">
            <Loader size="sm" role="status" aria-label={t('common:state.loading')} />
          </Center>
        }
      >
        <Outlet key={timeZoneVersion} />
      </Suspense>
    </>
  );
}
